import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Crown, Sparkles, Shield, Check } from "lucide-react";

export default function Membership() {
  const [tiers, setTiers] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/membership/tiers").then((r) => setTiers(r.data)).catch(() => {});
    if (user) {
      api.get("/membership/me").then((r) => setCurrent(r.data)).catch(() => {});
    }
  }, [user]);

  const subscribe = async (tierId) => {
    if (!user) {
      nav("/login");
      return;
    }
    if (!window.confirm(`Subscribe to ${tierId.toUpperCase()}? ${tiers.find((t) => t.id === tierId)?.price_usdt} USDT will be deducted from your wallet.`)) return;
    setBusy(tierId);
    try {
      const { data } = await api.post("/membership/subscribe", { tier: tierId });
      toast.success(`${tierId.toUpperCase()} active! Expires: ${new Date(data.expires_at).toLocaleDateString()}`);
      api.get("/membership/me").then((r) => setCurrent(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally {
      setBusy(null);
    }
  };

  const iconFor = (id) => id === "gold" ? Crown : id === "premium" ? Sparkles : Shield;
  const colorFor = (id) => id === "gold" ? "text-yellow-400 border-yellow-400" : id === "premium" ? "text-neon-green border-neon-green" : "text-white/60 border-neon-line";

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-10" data-testid="membership-page">
      <div className="label-mono mb-3">// 03_membership</div>
      <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-2">PICK YOUR TIER.</h1>
      <p className="text-white/60 font-body mb-2">Lower platform fees, higher cashback, more perks. Climb the tiers, earn more.</p>
      {current && (
        <div className="panel p-3 mb-8 inline-flex items-center gap-2 text-xs font-mono" data-testid="current-tier-pill">
          <span className="text-white/40">active plan:</span>
          <span className={current.tier === "gold" ? "text-yellow-400" : current.tier === "premium" ? "text-neon-green" : "text-white/70"}>
            {current.tier.toUpperCase()}
          </span>
          {current.expires_at && current.tier !== "basic" && (
            <span className="text-white/40">· expires: {new Date(current.expires_at).toLocaleDateString()}</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {tiers.map((t) => {
          const Icon = iconFor(t.id);
          const isActive = current?.tier === t.id;
          const isFree = t.price_usdt === 0;
          return (
            <div key={t.id} className={`panel p-6 lg:p-8 relative ${isActive ? "border-2 " + colorFor(t.id).split(" ")[1] : ""}`} data-testid={`tier-card-${t.id}`}>
              {isActive && (
                <div className={`absolute -top-3 left-6 px-3 py-0.5 label-mono ${t.id === "gold" ? "bg-yellow-400 text-black" : "bg-neon-green text-black"}`}>
                  ✓ ACTIVE
                </div>
              )}
              <div className={`w-14 h-14 border-2 ${colorFor(t.id)} grid place-items-center mb-5`}>
                <Icon className={`w-7 h-7 ${colorFor(t.id).split(" ")[0]}`} />
              </div>
              <h2 className="font-display font-black text-2xl tracking-tighter">{t.name}</h2>
              <div className="mt-3 mb-6">
                <span className="font-mono font-black text-4xl">{isFree ? "FREE" : `${t.price_usdt}`}</span>
                {!isFree && <span className="text-white/40 font-mono text-sm"> USDT/mo</span>}
              </div>
              <ul className="space-y-2 mb-8">
                {t.perks.map((perk, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm font-body text-white/80">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${colorFor(t.id).split(" ")[0]}`} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              {isFree ? (
                <Link to="/login" className="btn-ghost w-full text-center block" data-testid={`subscribe-${t.id}`}>
                  Start Free
                </Link>
              ) : isActive ? (
                <button onClick={() => subscribe(t.id)} disabled={busy === t.id} className="btn-ghost w-full" data-testid={`renew-${t.id}`}>
                  {busy === t.id ? "..." : "Extend (+30 days)"}
                </button>
              ) : (
                <button onClick={() => subscribe(t.id)} disabled={busy === t.id} className={`w-full font-display font-black uppercase tracking-wider py-3 transition-colors disabled:opacity-30 ${t.id === "gold" ? "bg-yellow-400 text-black hover:bg-white" : "bg-neon-green text-black hover:bg-white"}`} data-testid={`subscribe-${t.id}`}>
                  {busy === t.id ? "Processing..." : `${t.price_usdt} USDT · Subscribe`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 panel p-5 text-xs font-mono text-white/50">
        <div className="label-mono text-neon-green mb-2">// how it works</div>
        <ul className="space-y-1 list-disc list-inside">
          <li>Subscription fee is deducted instantly from your USDT wallet balance</li>
          <li>All perks of your chosen tier are active for 30 days</li>
          <li>When it expires you auto-downgrade to BASIC — renew anytime</li>
          <li>If you renew before expiry, time is added on top (no loss)</li>
          <li>Cashback is paid out automatically every Sunday</li>
        </ul>
      </div>
    </div>
  );
}
